import React, { useEffect, useState, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@store/StoreProvider';
import { toastStore } from '@store/ToastStore';
import {
  applyEmailNotVerifiedRestriction,
  readEmailVerifiedFlag,
} from '@utils/apiHelpers';
import { EMAIL_VERIFICATION_PROFILE_PATH } from '@store/EmailVerificationStore';
import { AuthPageShell } from '@app/auth/AuthPageShell';
import { RecaptchaNotice } from '@app/auth/RecaptchaNotice';
import { PasswordToggle } from '@app/auth/authIcons';
import { fetchWithRecaptchaRetry, preloadRecaptcha } from '@utils/recaptcha';

export default observer(function RegistrationPage() {
  const { i18n, auth } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const prefilledEmail = new URLSearchParams(location.search).get('email') || '';

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validatePasswordStrength = (value: string) => {
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    let isValid = true;
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      newErrors.email = i18n.t('errors.auth.enter_email');
      isValid = false;
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = i18n.t('errors.auth.invalid_email');
      isValid = false;
    }

    if (!password) {
      newErrors.password = i18n.t('errors.auth.password_required');
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = i18n.t('errors.auth.password_min_length');
      isValid = false;
    } else {
      const passwordCheck = validatePasswordStrength(password);
      if (!passwordCheck.isValid) {
        const missing = [];
        if (!passwordCheck.hasUpperCase) missing.push(i18n.t('auth.password_rules.uppercase'));
        if (!passwordCheck.hasLowerCase) missing.push(i18n.t('auth.password_rules.lowercase'));
        if (!passwordCheck.hasNumber) missing.push(i18n.t('auth.password_rules.digit'));
        newErrors.password = i18n.t('auth.password_rules.must_contain', { rules: missing.join(', ') });
        isValid = false;
      }
    }

    if (!confirm) {
      newErrors.confirm = i18n.t('auth.password.repeat');
      isValid = false;
    } else if (password && confirm !== password) {
      newErrors.confirm = i18n.t('errors.auth.passwords_mismatch');
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      toastStore.show(i18n.t('errors.fix_form'), 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithRecaptchaRetry('register', (captchaToken) =>
        fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmedEmail, password, captchaToken }),
        }),
      );

      const data = await response.json();

      if (response.ok) {
        toastStore.show(i18n.t('auth.success.registered_check_email'), 'success');
        try {
          const loginRes = await fetchWithRecaptchaRetry('login', (captchaToken) =>
            fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/login`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: trimmedEmail, password, captchaToken }),
            }),
          );
          const loginData = await loginRes.json();
          if (loginRes.ok) {
            if (loginData.token) auth.setToken(loginData.token);
            const emailVerified = readEmailVerifiedFlag(loginData);
            auth.setUser({
              id: loginData.id ?? data.id,
              email: loginData.email ?? data.email,
              role: loginData.role ?? data.role,
              createdAt: loginData.createdAt ?? data.createdAt,
              ...(emailVerified !== undefined ? { emailVerified } : {}),
            });
            if (emailVerified === false) {
              applyEmailNotVerifiedRestriction(
                loginData && typeof loginData === 'object'
                  ? (loginData as Record<string, unknown>)
                  : null,
              );
              navigate(EMAIL_VERIFICATION_PROFILE_PATH, { replace: true });
            } else {
              navigate('/control');
            }
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : i18n.t('errors.generic');
          toastStore.show(`${i18n.t('errors.title')}. ${message}`, 'error');
          console.error('Registration error:', err);
        }
        setEmail('');
        setPassword('');
        setConfirm('');
      } else {
        const errorMessage = data.message || data.error || i18n.t('errors.auth.register');
        toastStore.show(errorMessage, 'error');
        if (data.errors) {
          const apiErrors: Record<string, string> = {};
          if (data.errors.email) apiErrors.email = data.errors.email;
          if (data.errors.password) apiErrors.password = data.errors.password;
          setErrors(apiErrors);
        }
      }
    } catch (error) {
      const isCaptchaError = error instanceof Error && /reCAPTCHA|captcha/i.test(error.message);
      toastStore.show(
        isCaptchaError
          ? i18n.t('errors.generic')
          : i18n.t('errors.server_connection'),
        'error',
      );
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell title={i18n.t('auth.register.submit')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            className={`fld ${errors.email ? 'fld-error' : ''}`}
            placeholder={i18n.t('auth.work_email_placeholder')}
            autoComplete="email"
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              clearError('email');
            }}
          />
          {errors.email && <div className="auth-field-error">{errors.email}</div>}
        </div>

        <div>
          <div className="relative">
            <input
              id="pw1"
              type={showPassword ? 'text' : 'password'}
              className={`fld pr-12 ${errors.password ? 'fld-error' : ''}`}
              placeholder={i18n.t('auth.password.label')}
              autoComplete="new-password"
              value={password}
              onChange={(ev) => {
                setPassword(ev.target.value);
                clearError('password');
              }}
            />
            <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
          </div>
          {errors.password && <div className="auth-field-error">{errors.password}</div>}
        </div>

        <div>
          <div className="relative">
            <input
              id="pw2"
              type={showConfirm ? 'text' : 'password'}
              className={`fld pr-12 ${errors.confirm ? 'fld-error' : ''}`}
              placeholder={i18n.t('auth.password.repeat')}
              autoComplete="new-password"
              value={confirm}
              onChange={(ev) => {
                setConfirm(ev.target.value);
                clearError('confirm');
              }}
            />
            <PasswordToggle visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
          </div>
          {errors.confirm && <div className="auth-field-error">{errors.confirm}</div>}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-grad w-full h-[52px] rounded-2xl font-bold text-white shadow-xl shadow-brand/30"
          >
            {isLoading ? i18n.t('auth.loading.sign_in') : i18n.t('auth.sign_up')}
          </button>
          <RecaptchaNotice />
        </div>
      </form>

      <div className="mt-5 text-center text-sm text-slate-400">
        {i18n.t('auth.go_to_login')}{' '}
        <Link to="/login" className="text-brand2 font-semibold hover:underline">
          {i18n.t('app.actions.sign_in')}
        </Link>
      </div>
    </AuthPageShell>
  );
});
